import { Inject, Injectable } from '@angular/core';
import {
  CategoryModel,
  IStoreService,
  StorePageStore,
  STORE_SERVICE,
} from '@boxx/store-core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import {
  catchError,
  mergeMap,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import * as fromCategoriesActions from './store-categories.actions';

@Injectable()
export class StoreCategoriesEffects {
  // --------------------------- CATEGORIES: ---------------------------------
  loadCategories$ = createEffect(() =>
    this.actions.pipe(
      ofType(fromCategoriesActions.loadCategoriesBeginAction),
      switchMap(() =>
        this.service.loadCategories().pipe(
          mergeMap((payload) => [
            fromCategoriesActions.loadCategoriesSuccessAction({ payload }),
            fromCategoriesActions.clearCategoriesSuccessErrorDataAction(),
          ]),
          catchError((errors) => {
            console.error('StoreEffects.loadCategories$ ERROR', errors);
            return of([
              fromCategoriesActions.loadCategoriesFailAction({ errors }),
              fromCategoriesActions.clearCategoriesSuccessErrorDataAction(),
            ]).pipe(switchMap((d) => d));
          })
        )
      )
    )
  );

  loadCategoryById$ = createEffect(() =>
    this.actions.pipe(
      ofType(fromCategoriesActions.loadCategorieByIdBeginAction),
      switchMap((action: any) =>
        this.service.loadCategoryById(action.id).pipe(
          mergeMap((payload) => [
            fromCategoriesActions.loadCategorieByIdSuccessAction({ payload }),
            fromCategoriesActions.clearCategoriesSuccessErrorDataAction(),
          ]),
          catchError((errors) => {
            console.error('StoreEffects.loadCategoryById$ ERROR', errors);
            return of([
              fromCategoriesActions.loadCategorieByIdFailAction({ errors }),
              fromCategoriesActions.clearCategoriesSuccessErrorDataAction(),
            ]).pipe(switchMap((d) => d));
          })
        )
      )
    )
  );

  createCategory$ = createEffect(() =>
    this.actions.pipe(
      ofType(fromCategoriesActions.createCategoryBeginAction),
      withLatestFrom(this.storePageStore.categoriesStore.data$),
      switchMap(([action, categoriesData]: [any, CategoryModel[]]) =>
        this.service.createCategory((action as any).payload).pipe(
          mergeMap((data) => {
            const newItem = (action as any).payload;
            const newData: CategoryModel = {
              id: data.id,
              orderBy: 0,
              name: newItem.name,
              url: null,
              productCount: 0,
              parentId: newItem.parentId,
              description: newItem.description,
              enabled: true,
              isSampleCategory: false,
              image: null
            };
            const payload = [newData, ...categoriesData];
            return [
              fromCategoriesActions.createCategorySuccessAction({ payload }),
              fromCategoriesActions.clearCategoriesSuccessErrorDataAction(),
            ];
          }),
          catchError((errors) => {
            console.error('StoreEffects.createCategory$ ERROR', errors);
            return of([
              fromCategoriesActions.createCategoryFailAction({ errors }),
              fromCategoriesActions.clearCategoriesSuccessErrorDataAction(),
            ]).pipe(switchMap((d) => d));
          })
        )
      )
    )
  );

  updateCategory$ = createEffect(() =>
  this.actions.pipe(
    ofType(fromCategoriesActions.updateCategoryBeginAction),
    withLatestFrom(this.storePageStore.categoriesStore.data$),
    switchMap(([action, categoriesData]: [any, CategoryModel[]]) =>
      this.service.updateCategory((action as any).payload).pipe(
        mergeMap((data) => {
          const newItem = (action as any).payload;
          const newData: CategoryModel = {
            id: newItem.id,
            orderBy: 0,
            name: newItem.name,
            url: null,
            productCount: 0,
            parentId: newItem.parentId,
            description: newItem.description,
            enabled: true,
            isSampleCategory: false,
            image: null
          };
          const payload = newData;//[newData, ...categoriesData];
          return [
            fromCategoriesActions.updateCategorySuccessAction({ payload }),
            fromCategoriesActions.clearCategoriesSuccessErrorDataAction(),
          ];
        }),
        catchError((errors) => {
          console.error('StoreEffects.createCategory$ ERROR', errors);
          return of([
            fromCategoriesActions.updateCategoryFailAction({ errors }),
            fromCategoriesActions.clearCategoriesSuccessErrorDataAction(),
          ]).pipe(switchMap((d) => d));
        })
      )
    )
  )
);

  deleteCategory$ = createEffect(() =>
    this.actions.pipe(
      ofType(fromCategoriesActions.deleteCategoryBeginAction),
      withLatestFrom(this.storePageStore.categoriesStore.data$),
      switchMap(([action, categoriesData]: [any, CategoryModel[]]) =>
        this.service.deleteCategory(action.id).pipe(
          mergeMap(() => {
            const payload = categoriesData.filter((c) => c.id !== action.id);
            return [
              fromCategoriesActions.deleteCategorySuccessAction({ payload }),
              fromCategoriesActions.clearCategoriesSuccessErrorDataAction(),
            ];
          }),
          catchError((errors) => {
            console.error('StoreEffects.deleteCategory$ ERROR', errors);
            return of([
              fromCategoriesActions.deleteCategoryFailAction({ errors }),
              fromCategoriesActions.clearCategoriesSuccessErrorDataAction(),
            ]).pipe(switchMap((d) => d));
          })
        )
      )
    )
  );


  uploadImageCategory$ = createEffect(() =>
    this.actions.pipe(
      ofType(fromCategoriesActions.uploadImageCategoryBeginAction),
      withLatestFrom(this.storePageStore.categoriesStore.data$),
      switchMap(([action, categoriesData]: [any, CategoryModel[]]) =>
        this.service.uploadImageCategory(action.id, action.file).pipe(
          mergeMap((data) => {
                      const payload = [null, ...categoriesData];
                      return [
                        fromCategoriesActions.uploadImageCategorySuccessAction(null),
                        fromCategoriesActions.clearCategoriesSuccessErrorDataAction(),
                      ];
                    }),
          catchError((errors) => {
            console.error('StoreEffects.uploadImageCategory$ ERROR', errors);
            return of([
              fromCategoriesActions.uploadImageCategoryFailAction({ errors }),
              fromCategoriesActions.clearCategoriesSuccessErrorDataAction(),
            ]).pipe(switchMap((d) => d));
          })
        )
      )
    )
  );

  deleteImageCategory$ = createEffect(() =>
  this.actions.pipe(
    ofType(fromCategoriesActions.deleteImageCategoryBeginAction),
    withLatestFrom(this.storePageStore.categoriesStore.data$),
    switchMap(([action, categoriesData]: [any, CategoryModel[]]) =>
      this.service.deleteImageCategory(action.id).pipe(
        mergeMap(() =>
        {
          const catFilter = categoriesData.filter((c) => c.id === action.id);
          const newData: CategoryModel = {
            id: catFilter[0].id,
            orderBy: catFilter[0].orderBy,
            name: catFilter[0].name,
            url: catFilter[0].url,
            parentId: catFilter[0].parentId,
            productCount: catFilter[0].productCount,
            description: catFilter[0].description,
            enabled: catFilter[0].enabled,
            isSampleCategory: catFilter[0].isSampleCategory,
            image: ''
          };
          const payload = newData;

          return [
            fromCategoriesActions.deleteImageCategorySuccessAction({payload}),
            fromCategoriesActions.clearCategoriesSuccessErrorDataAction(),
          ];
        }
        ),
        catchError((errors) => {
          console.error('StoreEffects.deleteImageCategory$ ERROR', errors);
          return of([
            fromCategoriesActions.deleteImageCategoryFailAction({ errors }),
            fromCategoriesActions.clearCategoriesSuccessErrorDataAction(),
          ]).pipe(switchMap((d) => d));
        })
      )
    )
  )
);

getSingleCategory$ = createEffect(() =>
this.actions.pipe(
  ofType(fromCategoriesActions.loadImageCategoryUploadedBeginAction),
  withLatestFrom(this.storePageStore.categoriesStore.data$),
  switchMap(([action, categoriesData]: [any, any]) =>
    this.service.getSingleCategory(action.id).pipe(
      mergeMap((data) => {
                  const newData: CategoryModel = {
                    id: action.id,
                    orderBy: data.orderBy,
                    name: data.name,
                    url: data.url,
                    parentId: data.parentId,
                    productCount: data.productCount,
                    description: data.description,
                    enabled: data.enabled,
                    isSampleCategory: false,
                    image: data.imageUrl
                  };
                  const payload = newData;

                  return [
                    fromCategoriesActions.loadImageCategoryUploadedSuccessAction({payload}),
                    fromCategoriesActions.clearCategoriesSuccessErrorDataAction(),
                  ];
                }),
      catchError((errors) => {
        console.error('StoreEffects.getImageCategory$ ERROR', errors);
        return of([
          fromCategoriesActions.loadImageCategoryUploadedFailAction({ errors }),
          fromCategoriesActions.clearCategoriesSuccessErrorDataAction(),
        ]).pipe(switchMap((d) => d));
      })
    )
  )
)
);

  constructor(
    private actions: Actions,
    private storePageStore: StorePageStore,
    @Inject(STORE_SERVICE) private service: IStoreService
  ) {}
}
